import {BaseElementAsync, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {IDepotUiEnv} from "/@lib@/store/depot.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {OResViewerSingleInit, ResViewerSingle} from "/@back@/store/views/resViewer.js";
import {AccelKeyMgr} from "/@lib@/commons/actions.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IInfo, IInfoBroker, IInfoBrokerPointer, IInfoConsumer, IInfoProducer, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {IView, IViewApi, IViewsContainer, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {OSearchCritInit, SearchCrit} from "/@back@/store/views/searchCrit.js";
import {OSearchResultInit, SearchResult} from "/@back@/store/views/searchResult.js";
import {InfoResetSearchCrits, InfoSearchCritsChange, InfoSearchResults, ISearchEngine} from "/@lib@/store/search.js";

/**
 * Panneau combinant un searchCrit, un searchResult et un ResViewerSingle.
 */
export interface SearchBoard extends BaseElementAsync {
	initialize(init: OSearchBoardInit): this;
}

export interface OSearchBoardInit extends OSkinableInit, OLastDatasInit, IInfoBrokerPointer {
	reg?: IReg<IDepotUiEnv>

	/** Moteur d'execution de la requête. */
	searchEngine: ISearchEngine<any, any>

	/**
	 * Choix du layout.
	 * - C : Critères de recherche
	 * - R : Résultats en liste
	 * - V : View de la ressource sélectionnée dans les résultats.
	 *
	 * '-' = flex horizontal, '/' =  flex vertical.
	 */
	layout?: 'C-R/V' /*default*/ | 'C/R-V' | 'C/R'

	/** Critères de recherche. */
	searchCrit: OSearchCritInit

	/** Résultats en liste. */
	searchResult?: OSearchResultInit

	/** ResViewer. */
	resViewer?: OResViewerSingleInit
}

export class SearchBoard extends BaseElementAsync implements IView, IViewsContainer, IInfoConsumer {
	reg: IReg<IDepotUiEnv>;

	searchEngine: ISearchEngine<any, any>

	infoBroker: SearchBoardInfoBroker;

	accelKeyMgr: AccelKeyMgr<IRegPointer<IDepotUiEnv>>;

	protected async _initialize(init: OSearchBoardInit) {
		this.reg = REG.createSubReg(this.findReg(init));
		this.searchEngine = init.searchEngine;
		this.infoBroker = this.reg.env.infoBroker = new SearchBoardInfoBroker(this, init.infoBroker || this.reg.parentReg.env.infoBroker);
		this.infoBroker.addConsumer(this);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin('webzone:panel', sr);
		this._initAndInstallSkin(this.localName, init);

		this.accelKeyMgr = new AccelKeyMgr();
		this.addEventListener('keydown', function (this: SearchBoard, ev: KeyboardEvent) {
			this.accelKeyMgr.handleKeyboardEvent(ev, this)
		});
		this.accelKeyMgr.initFromMapActions(this.reg.getListAsMap("accelkeys:SearchBoard:global"));

		const searchCritInit = BASIS.newInit(init.searchCrit, this.reg);
		searchCritInit.lastDatas = init.lastDatas;
		searchCritInit.infoBroker = this.infoBroker;
		const searchCrit = new SearchCrit().initialize(searchCritInit);
		searchCrit.setAttribute("c-resizable", "");

		const resultInit = BASIS.newInit(init.searchResult, this.reg);
		resultInit.lastDatas = init.lastDatas;
		resultInit.infoBroker = this.infoBroker;
		const searchResult = new SearchResult().initialize(resultInit);
		searchResult.setAttribute("c-resizable", "");

		if (init.layout === "C/R") {
			this.setAttribute("orient", "v");
			sr.append(
				searchCrit,
				<c-resizer c-orient="column"/>,
				searchResult
			);
		} else {
			const resViewerInit = BASIS.newInit(init.resViewer, this.reg);
			resViewerInit.lastDatas = init.lastDatas;
			const resViewer = new ResViewerSingle().initialize(resViewerInit);
			this.reg.installSkin('webzone:page', resViewer.shadowRoot);
			if (init.layout === "C/R-V") {
				this.setAttribute("orient", "v");
				sr.append(
					searchCrit,
					<c-resizer c-orient="column"/>,
					<div id="main" class="h" c-resizable="">
						{searchResult}
						<c-resizer c-orient="row"/>
						{resViewer}
					</div>
				);
			} else {
				this.setAttribute("orient", "h");
				sr.append(
					searchCrit,
					<c-resizer c-orient="row"/>,
					<div id="main" class="v" c-resizable="">
						{searchResult}
						<c-resizer c-orient="column"/>
						{resViewer}
					</div>
				);
			}
		}
		await searchCrit.initializedAsync;
		await searchResult.initializedAsync;
		this.infoBroker.dispatchInfo(new InfoResetSearchCrits({}), this); //TODO props issu de lastDatas ?
	}

	onInfo(info: IInfo) {
		if (info instanceof InfoSearchCritsChange) {
			this.searchEngine.search(info.props).then((result) => {
				this.infoBroker.dispatchInfo(new InfoSearchResults(info.props, result), this);
			});
		}
	}

	shown: boolean;

	onViewShown() {
		this.shown = true;
		VIEWS.onContainerShown(this);
	}

	onViewHidden(closed?: boolean) {
		this.shown = false;
		if (closed) this.infoBroker.close();
		VIEWS.onContainerHidden(this, closed);
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		if (options?.visible && !this.shown) return;
		return VIEWS.visitDescendants(this.shadowRoot, visitor);
	}

	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (options?.visible && !this.shown) return;
		return VIEWS.visitDescendantsAsync(this.shadowRoot, visitor);
	}
}


REG.reg.registerSkin('store-search-board', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		padding: 5px;
	}

	:host([orient=v]) {
		flex-direction: column;
	}

	[hidden] {
		display: none;
	}

	.h {
		flex-direction: row;
	}

	.v {
		flex-direction: column;
	}

	store-ut-browser {
		flex: 1 1 5em;
		border: 1px solid var(--border-color);
		border-radius: 7px;
	}

	c-resizer[c-orient=row] {
		width: 10px;
		background-color: transparent;
	}

	c-collapser {
		background-color: var(--panel-bgcolor);
	}

	#main {
		flex: 3 1 20em;
		display: flex;
		border: 1px solid var(--border-color);
		border-radius: 7px;
		overflow: hidden;
	}

	store-resviewer-single {
		flex: 1;
	}
`);

customElements.define('store-search-board', SearchBoard);

class SearchBoardInfoBroker extends InfoBrokerBasic implements IInfoConsumer {
	constructor(public view: SearchBoard, public parent: IInfoBroker) {
		super();
		if (parent) parent.addConsumer(this);
	}

	dispatchInfo(info: IInfo, from: IInfoProducer) {
		super.dispatchInfo(info, from);
		if (this.parent && info.infoHolders.indexOf(this) < 0) {
			this.parent.dispatchInfo(info, this);
		}
	}

	close() {
		this.parent?.removeConsumer(this);
	}

	//Info from parent
	onInfo(info: IInfo) {
		if (this.view.shown) this.dispatchInfo(info, this);
	}
}